import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TableRow,
  TableCell,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  CircularProgress
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { YourPerformanceCard } from 'src/content/localSeo/YourPerformanceCard';
import { pdfContainerSx } from 'src/common/PdfComponents/PdfSxs';
import { MapRankResultCards } from 'src/content/mapRankTracker/MapRankResultCards';
import { LocalSeoPDFCSS, SideBarCSS } from '../styles/style';
import { PDF_TEXTS } from '../utils/texts';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Shape of a single keyword object inside localSeoReportData.mapRank.keywords
 */
interface KeywordEntry {
  keyword: string;
  avg_rank?: number;
  num_points?: number;
  visibility?: number;
  grid_points?: Array<Record<string, any>>;
  competitors?: Array<Record<string, any>>;
  [key: string]: any;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Adapts a raw keyword entry from the consolidated API into the shape that
 * MapRankResultCards expects as its `reportData` prop.
 */
const adaptKeywordToReportData = (
  keyword: KeywordEntry,
  mapRankBase: Record<string, any>
): Record<string, any> => ({
  ...mapRankBase,
  summary: {
    avg_rank: keyword.avg_rank,
    num_points: keyword.num_points
  },
  runComparison: keyword.runComparison || null,
  visibility: keyword.visibility,
  grid_points: (keyword.grid_points ?? []).map((p: any) => ({
    ...p,
    latitude: p.latitude ?? p.lat,
    longitude: p.longitude ?? p.long
  })),
  keywords: [{ ...(keyword || {}), name: keyword?.keyword || '' }]
});

// ---------------------------------------------------------------------------
// Shared competitor table UI
// ---------------------------------------------------------------------------

const getVisibilityColor = (value: number) => {
  if (value === 0 || !value) return '#FFFFFF'; // white instead of transparent to render something
  if (value >= 90) return '#33DBBA'; // green
  if (value >= 80) return '#BEDA1E'; // lime
  if (value >= 70) return '#F6E314'; // yellow
  if (value <= 50) return '#E64420'; // red-orange
  return '#ED9D1D';
};

const getArpColor = (value: number) => {
  if (value === 0 || !value) return '#FFFFFF'; // white
  if (value <= 2.0) return '#33DBBA';
  if (value <= 3.0) return '#BEDA1E';
  if (value <= 5.0) return '#F6E314';
  return '#ED9D1D';
};

const CompetitorTable = ({
  competitors,
  title
}: {
  competitors: any[];
  title: ReactNode;
}) => (
  <Card
    className="LocalSeoKeywordTable force-page-break"
    sx={SideBarCSS.competitorTableCard}
  >
    <CardContent sx={SideBarCSS.competitorTableCardContent}>
      <Typography sx={SideBarCSS.competitorTableTitle}>{title}</Typography>

      <Table sx={SideBarCSS.competitorTable}>
        <TableHead>
          <TableRow sx={SideBarCSS.competitorTableHeaderRow}>
            <TableCell sx={SideBarCSS.competitorTableCellIndex}>
              <Typography sx={pdfContainerSx?.tableHeadText}>
                {PDF_TEXTS.localSeo.tableHeaders.index}
              </Typography>
            </TableCell>
            <TableCell sx={SideBarCSS.competitorTableCellNameWidth}>
              <Typography sx={pdfContainerSx?.tableHeadText}>
                {PDF_TEXTS.localSeo.tableHeaders.businessName}
              </Typography>
            </TableCell>
            <TableCell sx={SideBarCSS.competitorTableCellRatingWidth}>
              <Typography sx={pdfContainerSx?.tableHeadText}>
                {PDF_TEXTS.localSeo.tableHeaders.rating}
              </Typography>
            </TableCell>
            <TableCell sx={SideBarCSS.competitorTableCellVisibilityWidth}>
              <Typography sx={pdfContainerSx?.tableHeadText}>
                Visibility
              </Typography>
            </TableCell>
            <TableCell sx={SideBarCSS.competitorTableCellArpWidth}>
              <Typography sx={pdfContainerSx?.tableHeadText}>ARP</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {competitors?.map((row, index) => (
            <TableRow
              key={`${row.title}-${index}`}
              sx={SideBarCSS.competitorBodyRow}
            >
              <TableCell sx={SideBarCSS.competitorTableCell}>
                <Typography sx={SideBarCSS.competitorCellIndex}>
                  {index + 1}
                </Typography>
              </TableCell>
              <TableCell sx={SideBarCSS.competitorTableCell}>
                <Typography sx={SideBarCSS.competitorCellName}>
                  {row.title}
                </Typography>
                {row.primary_category && (
                  <Box sx={LocalSeoPDFCSS.categoryText}>
                    {PDF_TEXTS.localSeo.primaryCategory} {row.primary_category}
                    {row?.additional_categories?.filter(Boolean)?.length >
                      0 && (
                      <>
                        <span> {PDF_TEXTS.localSeo.secondaryCategory}</span>{' '}
                        {row?.additional_categories
                          ?.filter(Boolean)
                          ?.join(', ')}
                      </>
                    )}
                  </Box>
                )}
              </TableCell>
              <TableCell sx={SideBarCSS.competitorTableCell}>
                <Typography
                  sx={{
                    ...SideBarCSS.competitorCellValue
                  }}
                >
                  {row.review_rating || 0}
                </Typography>
                <Box sx={LocalSeoPDFCSS.categoryText}>
                  ({row.num_reviews || 0}{' '}
                  {PDF_TEXTS.localSeo.tableHeaders.reviews})
                </Box>
              </TableCell>
              <TableCell sx={SideBarCSS.competitorTableCell}>
                <Box sx={SideBarCSS.competitorTableContainer}>
                  {row.visibility !== undefined && row.visibility !== null ? (
                    <>
                      <Box
                        sx={LocalSeoPDFCSS.visibilityDot(
                          getVisibilityColor(row.visibility)
                        )}
                      />
                      <Typography sx={SideBarCSS.competitorCellValue}>
                        {row.visibility}%
                      </Typography>
                    </>
                  ) : (
                    <Box sx={LocalSeoPDFCSS.dashPlaceholder} /> // Dash Placeholder
                  )}
                  {(row.visibility === undefined ||
                    row.visibility === null) && (
                    <Typography sx={SideBarCSS.competitorCellValue}>
                      -
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell sx={SideBarCSS.competitorTableCell}>
                <Box sx={SideBarCSS.competitorTableContainer}>
                  {row.avg_rank !== undefined && row.avg_rank !== null ? (
                    <>
                      <Box
                        sx={LocalSeoPDFCSS.visibilityDot(
                          getArpColor(row.avg_rank)
                        )}
                      />
                      <Typography sx={SideBarCSS.competitorCellValue}>
                        {row.avg_rank}
                      </Typography>
                    </>
                  ) : (
                    <Typography sx={SideBarCSS.competitorCellValue}>
                      -
                    </Typography>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

/**
 * Renders the MapRankResultCards for a single keyword when
 * `includeAllKeywords` is enabled.
 *
 * It temporarily patches `getReportDetails` in the Redux store via
 * a simple re-use of the existing `MapRankResultCards` by passing
 * the adapted reportData as a prop.
 */
const KeywordSection = ({
  keyword,
  mapRankBase,
  isExporting,
  isPdfBuilder
}: {
  keyword: KeywordEntry;
  mapRankBase: Record<string, any>;
  isExporting: boolean;
  isPdfBuilder?: boolean;
}) => {
  const reportData = useMemo(
    () => adaptKeywordToReportData(keyword, mapRankBase),
    [keyword, mapRankBase]
  );

  return (
    <Box sx={SideBarCSS.keywordSectionWrapper}>
      <MapRankResultCards
        reportData={reportData}
        mapDashboard={true}
        isExporting={isExporting}
        isPdfBuilder={isPdfBuilder}
      />
      {keyword?.competitors && keyword?.competitors?.length > 0 && (
        <CompetitorTable
          competitors={keyword?.competitors}
          title={
            <>
              <Box component="span" sx={SideBarCSS.competitorTitleKeyword}>
                {keyword?.keyword || keyword?.name || ''}
              </Box>
              <Box component="span" sx={SideBarCSS.competitorTitleSuffix}>
                {PDF_TEXTS.localSeo.competitorsSuffix}
              </Box>
            </>
          }
        />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main container
// ---------------------------------------------------------------------------

export const LocalSeoPDFContainer = ({
  isExporting = false,
  isPdfBuilder = true,
  error = null,
  loading = false,
  reportMetaData
}: {
  isExporting?: boolean;
  isPdfBuilder?: boolean;
  error?: string | null;
  loading?: boolean;
  reportMetaData?: any;
}) => {

  const rawFilteredLocationsData = useSelector(
    (state: RootState) => state.localSeo.reduxFilteredLocationsData
  );

  const selectedBusinessName = useSelector(
    (state: RootState) => state.localSeo.selectedBusinessName
  );

  const localSeoReportData = useSelector(
    (state: RootState) => state.dashboard.localSeoReportData
  );

  const pdfConfig = useSelector(
    (state: RootState) => state.dashboard.pdfConfig
  );

  const pdfReportData = useSelector(
    (state: RootState) => state.dashboard.pdfReportData
  );

  const reduxFilteredLocationsData = useMemo(() => {
    return (rawFilteredLocationsData || []).filter(
      (loc: any) => loc?.isDelete !== true
    );
  }, [rawFilteredLocationsData]);

  const reduxLocationsData = useSelector(
    (state: RootState) => state.localSeo.reduxLocationsData
  );

  const isCurrentLocalSeoReportDeleted = useMemo(() => {
    if (!localSeoReportData?.mapRank) return false;
    const businessName = localSeoReportData.mapRank.businessName;
    const locationId = localSeoReportData.mapRank.locationId;

    // Find in all locations
    const matchedLoc = (reduxLocationsData || []).find((loc: any) => {
      const locIdClean = loc.name?.replace('locations/', '');
      return locIdClean === locationId || loc.title === businessName;
    });

    return matchedLoc?.isDelete === true;
  }, [localSeoReportData, reduxLocationsData]);

  // Resolve includeAllKeywords: sidebar (pdfConfig) takes precedence,
  // then fall back to what the server stored in config.
  const includeAllKeywords: boolean =
    pdfConfig?.includeAllKeywords ??
    pdfReportData?.config?.local_seo_options?.includeAllKeywords ??
    false;

  const [match, setMatch] = useState<any>(null);

  useEffect(() => {
    if (localSeoReportData?.mapRank) {
      setMatch({
        title: localSeoReportData.mapRank.businessName,
        progressing: false
      });
    } else if (Array.isArray(reduxFilteredLocationsData)) {
      const found = reduxFilteredLocationsData.find(
        (location: any) => location?.title === selectedBusinessName
      );
      setMatch(found || reduxFilteredLocationsData[0]);
    }
  }, [localSeoReportData, reduxFilteredLocationsData, selectedBusinessName]);

  const showLocalMap = useSelector(
    (state: RootState) => state.localSeo.showLocalMap
  );
  const locationListLoader = useSelector(
    (state: RootState) => state.localSeo.locationsDetailsLoader
  );

  // ── Loading state ───────────────────────────────────────────────────────
  // Simplest logic: only show loading UI while the API 'loading' prop is true.
  if (loading && !isExporting) {
    return (
      <Box sx={SideBarCSS.localSeoLoadingBox}>
        <CircularProgress size={40} sx={SideBarCSS.localSeoSpinner} />
        <Typography sx={SideBarCSS.localSeoLoadingText}>
          {PDF_TEXTS.localSeo.fetchingDetails}
        </Typography>
      </Box>
    );
  }

  if (error && !isExporting) {
    return (
      <Box sx={SideBarCSS.localSeoErrorBox}>
        <Typography sx={SideBarCSS.localSeoErrorText}>{error}</Typography>
      </Box>
    );
  }

  if (isExporting && !localSeoReportData) {
    return null;
  }

  if (
    (!reduxFilteredLocationsData || reduxFilteredLocationsData.length === 0) &&
    !localSeoReportData
  ) {
    return null;
  }

  if (isCurrentLocalSeoReportDeleted) {
    return null;
  }

  // Determine if we should hide the Map Ranking section
  // In PDF mode, we want to be more permissive to ensure capture
  const hideMapRanking = showLocalMap !== 1 && !isExporting;

  return (
    <Box sx={LocalSeoPDFCSS.container(isExporting, hideMapRanking)}>
      <Box className="YourPerformanceCardSection">
        <Box className="LocalSeoPDFDashBoardWrapper keep-together">
          <YourPerformanceCard
            key={selectedBusinessName}
            locationsData={reduxFilteredLocationsData}
            isExporting={isExporting} // Pass the actual exporting state
            isPdfBuilder={isPdfBuilder}
          />
        </Box>
      </Box>

      <LocalSeoKeywordSections
        isExporting={isExporting}
        isPdfBuilder={isPdfBuilder}
        includeAllKeywords={includeAllKeywords}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Keyword sections (cards + map, repeated per keyword when flag is true)
// ---------------------------------------------------------------------------

const LocalSeoKeywordSections = ({
  isExporting,
  isPdfBuilder,
  includeAllKeywords
}: {
  isExporting: boolean;
  isPdfBuilder?: boolean;
  includeAllKeywords: boolean;
}) => {
  const localSeoReportData = useSelector(
    (state: RootState) => state.dashboard.localSeoReportData
  );

  // Derive the keywords array from the consolidated API response
  const keywords: KeywordEntry[] = useMemo(() => {
    try {
      return localSeoReportData?.mapRank?.keywords ?? [];
    } catch (err) {
      console.error(
        '[LocalSeoPDFContainer] Failed to read keywords from localSeoReportData:',
        err
      );
      return [];
    }
  }, [localSeoReportData]);

  if (!localSeoReportData?.mapRank) {
    return null;
  }

  const mapRankBase = localSeoReportData.mapRank;

  return (
    <>
      {keywords?.map((keyword, index) => (
        <Box
          key={`keyword-section-${index}`}
          className="YourPerformanceCardSection localSeoKeywordSectionBox"
          sx={SideBarCSS.localSeoKeywordSectionBox}
        >
          <KeywordSection
            keyword={keyword}
            mapRankBase={mapRankBase}
            isExporting={isExporting}
            isPdfBuilder={isPdfBuilder}
          />
        </Box>
      ))}
    </>
  );
};
